import { useEffect } from "react";

const SITE = "https://elgasplumbing.co.uk";
const DEFAULT_IMG = `${SITE}/icon-512.png`;
const JSONLD_ID = "page-jsonld";

function setMeta(selector, attr, value) {
  if (value === undefined || value === null) return;
  const el = document.head.querySelector(selector);
  if (el) {
    el.setAttribute(attr, value);
  }
}

function upsertMeta(selectorAttr, selectorValue, attr, content) {
  if (content === undefined || content === null) return;
  const sel = `meta[${selectorAttr}="${selectorValue}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(selectorAttr, selectorValue);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
}

/**
 * Per-route SEO: mutates document.head tags in place (no duplicates).
 * React 19 + react-helmet-async duplicate static <head> tags; this component
 * manages canonical / title / description / og:* / twitter:* / FAQ JSON-LD
 * directly against the DOM so each route ends up with exactly ONE of each.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMG,
  jsonLd,
}) {
  const url = `${SITE}${path}`;

  useEffect(() => {
    if (title) document.title = title;

    setMeta('link[rel="canonical"]', "href", url);
    upsertMeta("name", "description", "content", description);
    upsertMeta("property", "og:title", "content", title);
    upsertMeta("property", "og:description", "content", description);
    upsertMeta("property", "og:url", "content", url);
    upsertMeta("property", "og:image", "content", image);
    upsertMeta("name", "twitter:title", "content", title);
    upsertMeta("name", "twitter:description", "content", description);
    upsertMeta("name", "twitter:image", "content", image);

    // Page-specific JSON-LD (e.g. FAQPage). Replaced each navigation.
    const existing = document.getElementById(JSONLD_ID);
    if (existing) existing.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = JSONLD_ID;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById(JSONLD_ID);
      if (s) s.remove();
    };
  }, [title, description, url, image, jsonLd]);

  return null;
}
